// useAstriaMexicoTab.ts
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import i18n from "../../../i18n";
import { getAppLanguage } from "../../../utils/appLanguage";
import { api } from "../../../services/api";
import { Message, AstriaMexicoData } from "../../../contexts/ChatContext";
import { useCategories } from "../../../contexts/CategoryContext";
import { useSubCategories } from "../../../contexts/SubCategoryContext";

const CATEGORY_NAME = "Astria Mexico";

// ─── Simple, linear flow for the Astria Mexico module tabs:
//
//   Fill form → Click Reveal → API call → receive response → show it
//
// Deliberately bypasses ChatContext's chats/currentChat list and calls the
// streaming endpoint directly, reading the response it receives — same
// pattern as useAstriaUKV2Tab.ts. "Continue in Chat" hands off to the
// module's own Companion Talk tab rather than the persisted /sanctuary chat.

interface UseAstriaMexicoTabOptions {
  subCategoryName: string;
}

interface UseAstriaMexicoTabReturn {
  loading: boolean;
  error: string | null;
  message: Message | null;
  send: (text: string) => Promise<void>;
  reset: () => void;
  goToChat: () => Promise<void>;
}

export function useAstriaMexicoTab({
  subCategoryName,
}: UseAstriaMexicoTabOptions): UseAstriaMexicoTabReturn {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const { subCategories } = useSubCategories();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<Message | null>(null);

  const resolveIds = () => {
    const category = categories.find((c) => c.name === CATEGORY_NAME);
    const categoryId = category?.id || null;
    const subCategory = subCategories.find(
      (sc) => sc.categoryId === categoryId && sc.name === subCategoryName,
    );
    return { categoryId, subCategoryId: subCategory?.id || null };
  };

  const send = async (text: string) => {
    setLoading(true);
    setError(null);
    setMessage(null);

    const { categoryId, subCategoryId } = resolveIds();

    if (!categoryId) {
      setError(i18n.t("mx.common.errNotSetup"));
      setLoading(false);
      return;
    }
    if (!subCategoryId) {
      setError(i18n.t("mx.common.errNotSetup"));
      setLoading(false);
      return;
    }

    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      const baseUrl = api.defaults.baseURL || "";

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${baseUrl}/backend/chat/create?stream=true`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          userMessage: text,
          categoryId,
          subCategoryId,
          userId: userId || undefined,
        }),
      });

      if (!response.ok || !response.body) {
        let errMsg = i18n.t("mx.common.errServer");
        try {
          const errData = await response.json();
          if (errData?.message && getAppLanguage() === "en") errMsg = errData.message;
        } catch {
          // ignore — keep default message
        }
        setError(errMsg);
        setLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let content = "";
      let astriaMexicoData: AstriaMexicoData | null = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          for (const line of part.split("\n")) {
            if (!line.startsWith("data:")) continue;
            const dataStr = line.replace(/^data:\s*/, "");
            if (!dataStr) continue;

            let payload: any;
            try {
              payload = JSON.parse(dataStr);
            } catch {
              continue;
            }

            if (payload?.text) {
              content += payload.text;
            }
            if (payload?.done && payload?.astriaMexicoData) {
              astriaMexicoData = payload.astriaMexicoData;
            }
          }
        }
      }

      if (!content.trim()) {
        setError(i18n.t("mx.common.errNoReading"));
        setLoading(false);
        return;
      }

      setMessage({
        id: `ai_${Date.now()}`,
        content,
        sender: "ai",
        timestamp: new Date(),
        categoryId,
        subCategoryId,
        astriaMexicoData: astriaMexicoData || undefined,
      });
    } catch (err) {
      console.error(`Astria Mexico (${subCategoryName}) error:`, err);
      setError(i18n.t("mx.common.errServer"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessage(null);
    setError(null);
  };

  const goToChat = async () => {
    navigate("/astria-mexico/companion-talk");
  };

  return { loading, error, message, send, reset, goToChat };
}
